/**
 * Modules & Curriculum — the content-library pages under one nav entry.
 *
 * Modules, curricula, concepts and books are four views of the same thing: what
 * we teach and how it is organised. As four sidebar entries they pushed the rest
 * of Content off the fold and made you leave the page to answer a question that
 * spans two of them.
 *
 * The host stays thin on purpose: it owns which tab is showing and nothing else,
 * so each tab keeps the state it already had rather than lifting it up here.
 *
 * The tab lives in `?tab=` so a tab is linkable and Back steps between tabs
 * rather than out of the page. Detail routes (`/curriculum/:id`, `/books/:id`)
 * are untouched and still stand on their own.
 */

import { useSearchParams } from 'react-router-dom';
import { TabStrip } from '@shared-ui/components';
import { authApi } from '../api/auth';
import { effectiveRoleNames } from '../config/adminRoles';
import { useIsNonPublicTenant } from '../hooks/useMyTenant';
import { Modules } from './Modules';
import { CurriculaList } from './CurriculaList';
import { Concepts } from './Concepts';
import { Books } from './Books';
import { StudyPlans } from './StudyPlans';
import { PlatformExams } from './PlatformExams';

type TabKey = 'modules' | 'curriculum' | 'concepts' | 'books' | 'exams' | 'study-plans';

const DEFAULT_TAB: TabKey = 'modules';

export function ModulesCurriculum() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Concepts was a content_admin-only page while the other three admit
  // tenant_admin. Merging the nav entries must not widen that: the merged item
  // carries the union so a tenant admin still reaches modules and curricula,
  // and the Concepts tab is withheld separately rather than riding along.
  const user = authApi.getUser();
  const roles = effectiveRoleNames(user);
  const canSeeConcepts = !!user?.isSuperAdmin || roles.includes('content_admin');
  // Study Plans was its own nav entry at content_admin only, and folding it in
  // must not widen that any more than merging Concepts did — same rule, withheld
  // separately rather than riding on the merged entry's union.
  const canSeeStudyPlans = canSeeConcepts;
  // The book catalogue is the platform's own record of what it has ingested —
  // a school has no use for it and cannot add to it, so it is withheld whole
  // rather than shown read-only. Supersedes the VIEW-ONLY row in
  // docs/FeatureDesigns/multi-tenancy/tenant-admin-console.md §4.
  const isNonPublicTenant = useIsNonPublicTenant();
  const canSeeBooks = !isNonPublicTenant;

  const tabs = [
    { key: 'modules' as const, label: 'Modules & Topics' },
    { key: 'curriculum' as const, label: 'Curriculum' },
    ...(canSeeConcepts ? [{ key: 'concepts' as const, label: 'Concepts' }] : []),
    ...(canSeeBooks ? [{ key: 'books' as const, label: 'Books' }] : []),
    // Beside Books for the same reason Books is here: a plan is content bound to
    // a CURRICULUM, not to a class. Most students are in no class at all, so a
    // platform plan for CBSE-10 maths is the only plan they have to take — and
    // it belongs where its syllabus is authored.
    // Before Study Plans because a plan is scheduled AGAINST these dates — the
    // datesheet is the fixed point, the plan is what bends around it.
    ...(canSeeStudyPlans ? [{ key: 'exams' as const, label: 'Exams' }] : []),
    ...(canSeeStudyPlans ? [{ key: 'study-plans' as const, label: 'Study Plans' }] : []),
  ];

  const requested = searchParams.get('tab') as TabKey | null;
  // An unknown or withheld tab falls back rather than rendering blank — a stale
  // link, or a tenant admin handed `?tab=concepts`, still lands somewhere real.
  const active: TabKey = tabs.some(t => t.key === requested) ? requested! : DEFAULT_TAB;

  const selectTab = (key: TabKey): void => {
    // `replace` so stepping back leaves the page instead of walking every tab
    // the user happened to click through.
    setSearchParams(key === DEFAULT_TAB ? {} : { tab: key }, { replace: true });
  };

  return (
    <div className="modules-curriculum-page">
      <TabStrip
        ariaLabel="Content library views"
        tabs={tabs}
        active={active}
        onChange={selectTab}
      />
      {active === 'modules' && <Modules />}
      {active === 'curriculum' && <CurriculaList />}
      {active === 'concepts' && canSeeConcepts && <Concepts />}
      {active === 'books' && canSeeBooks && <Books />}
      {active === 'exams' && canSeeStudyPlans && <PlatformExams />}
      {active === 'study-plans' && canSeeStudyPlans && <StudyPlans />}
    </div>
  );
}
